import { routeAccessMap } from "core/components/PagePermissionChecker/RouteAccessMap";
import type { PitstopRoutes } from "core/entities/AppTypes";
import { usePermissionChecker } from "core/hooks/usePermissionChecker";
import { NotFound404Page } from "core/pages";
import { Unauthorized401Page } from "core/pages/Unauthorized401Page";
import { getBrand } from "core/utils/getBrand";
import { useRouter } from "next/router";
import type { ReactNode } from "react";
import { useFeatureFlagByRoute } from "../useFeatureFlags";

export function PagePermissionChecker({ children }: { children: ReactNode }) {
  const { hasSomePermissions, hasAllPermissions } = usePermissionChecker();
  const { pathname } = useRouter();
  const { brandCode } = getBrand();
  const isDevUser = hasSomePermissions(["Fixhub.DevOptions"]);
  const pageConfig = routeAccessMap.get(pathname as PitstopRoutes);
  const featureFlagResult = useFeatureFlagByRoute(pathname as PitstopRoutes);
  if (featureFlagResult.isFeatureFlagRoute && !featureFlagResult.isEnabled) {
    return (
      <Unauthorized401Page
        alertMessage={
          isDevUser
            ? `Page "${pathname}" is disabled via feature flag "${featureFlagResult.featureFlagKey}".`
            : undefined
        }
      />
    );
  }
  if (!pageConfig) {
    return <>{children}</>;
  }

  if (pageConfig.disabled) {
    return (
      <Unauthorized401Page
        alertMessage={
          isDevUser
            ? `Page "${pathname}" is disabled via RouteAccessMap`
            : undefined
        }
      />
    );
  }

  if (
    pageConfig.hasSomePermissions &&
    !hasSomePermissions(pageConfig.hasSomePermissions)
  ) {
    return (
      <Unauthorized401Page
        alertMessage={
          isDevUser
            ? `One of the following permission required: ${pageConfig.hasSomePermissions.join(", ")}`
            : undefined
        }
      />
    );
  }

  if (
    pageConfig.hasAllPermissions &&
    !hasAllPermissions(pageConfig.hasAllPermissions)
  ) {
    return (
      <Unauthorized401Page
        alertMessage={
          isDevUser
            ? `All of the following permission required: ${pageConfig.hasAllPermissions.join(", ")}`
            : undefined
        }
      />
    );
  }

  if (
    pageConfig.availableForBrands &&
    !pageConfig.availableForBrands.includes(brandCode)
  ) {
    return (
      <NotFound404Page
        alertMessage={
          isDevUser
            ? `Brand "${brandCode}" not in allowed brands: ${pageConfig.availableForBrands.join(", ")}`
            : undefined
        }
      />
    );
  }

  return <>{children}</>;
}
